import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import {
  Lightbulb,
  Target,
  Users,
  Eye,
  Sparkles,
  Heart,
  Globe,
  Code,
  ArrowRight,
} from 'lucide-react';

export function AboutPage() {
  const { t } = useTranslation();

  const values = [
    { key: 'innovation', icon: Lightbulb, color: 'from-purple-500 to-violet-500' },
    { key: 'quality', icon: Target, color: 'from-blue-500 to-cyan-500' },
    { key: 'community', icon: Users, color: 'from-green-500 to-emerald-500' },
    { key: 'transparency', icon: Eye, color: 'from-orange-500 to-amber-500' },
  ];

  const stats = [
    { value: '50K+', label: t('about.stats.developers') },
    { value: '120+', label: t('about.stats.countries') },
    { value: '10M+', label: t('about.stats.linesOfCode') },
    { value: '10+', label: t('about.stats.yearsExperience') },
  ];

  return (
    <>
      <SEO {...SEO_CONFIG.about} />
      <div className="min-h-screen">
      {/* Hero */}
      <section className="section-lg relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />

        <div className="container relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm mb-6">
              <Heart size={16} />
              <span>{t('about.badge')}</span>
            </div>

            <h1 className="heading-xl mb-6">
              {t('about.title')}{' '}
              <span className="text-accent">{t('about.titleHighlight')}</span>
            </h1>
            <p className="text-lg text-text-secondary">
              {t('about.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 border-y border-border-default bg-bg-surface">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-accent mb-1">{stat.value}</div>
                <div className="text-text-secondary text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="heading-lg mb-4">{t('about.mission.title')}</h2>
              <p className="text-text-secondary text-lg">
                {t('about.mission.description')}
              </p>
            </div>
            <div className="relative">
              <div className="aspect-video rounded-2xl bg-gradient-to-br from-accent/20 to-purple-500/20 border border-border-default flex items-center justify-center">
                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-accent to-purple-600 flex items-center justify-center">
                  <Sparkles className="text-white" size={48} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-lg mb-8 text-center">{t('about.story.title')}</h2>
            <div className="space-y-6 text-text-secondary">
              <p className="text-lg">{t('about.story.p1')}</p>
              <p className="text-lg">{t('about.story.p2')}</p>
              <p className="text-lg">{t('about.story.p3')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">{t('about.values.title')}</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value) => (
              <div key={value.key} className="card p-6 text-center hover:border-accent/30 transition-colors">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${value.color} flex items-center justify-center mx-auto mb-4`}>
                  <value.icon className="text-white" size={28} />
                </div>
                <h3 className="font-semibold mb-2">{t(`about.values.${value.key}.title`)}</h3>
                <p className="text-text-secondary text-sm">{t(`about.values.${value.key}.description`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <div className="card p-8 md:p-12 bg-gradient-to-br from-accent/10 to-purple-500/5 border-accent/20">
              <Globe className="mx-auto mb-6 text-accent" size={48} />
              <h2 className="heading-lg mb-4">{t('about.cta.title')}</h2>
              <p className="text-text-secondary mb-8">{t('about.cta.subtitle')}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/careers" className="btn btn-primary">
                  {t('about.cta.careers')}
                  <ArrowRight size={18} />
                </Link>
                <Link to="/contact" className="btn btn-secondary">
                  {t('about.cta.contact')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
